class CartDrawer extends HTMLElement {
  constructor() {
    super();
    window.slideCartOpened = false;
    this.addEventListener(
      'keyup',
      (evt) => {
        if (evt.code !== 'Escape') return;
        if (this.subscriptionConfirmation?.open || this.ignoreSubscriptionEscape) {
          this.ignoreSubscriptionEscape = false;
          return;
        }
        this.close();
      },
    );
    this.querySelector('#CartDrawer-Overlay').addEventListener(
      'click',
      this.close.bind(this),
    );
    this.addEventListener('change', this.handleKitUpsellChange.bind(this));
    this.addEventListener('submit', this.handleCheckoutSubmit.bind(this));
    this.addEventListener('click', this.handleExpressCheckoutClick.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupSubscriptionConfirmation();
    this.handleLegacySubscriptionConfirmation =
      this.handleLegacySubscriptionConfirmation.bind(this);
  }

  setupSubscriptionConfirmation() {
    this.subscriptionConfirmation = this.querySelector('#CartSubscriptionConfirmation');
    const dialog = this.subscriptionConfirmation;
    if (!dialog) return;

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeSubscriptionConfirmation();
    });
    dialog.addEventListener('keydown', (event) => {
      event.stopPropagation();
      if (event.key === 'Tab') {
        const buttons = dialog.querySelectorAll('button:not([disabled])');
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.ignoreSubscriptionEscape = true;
      this.closeSubscriptionConfirmation();
    });
    dialog.addEventListener('keyup', (event) => event.stopPropagation());
    dialog.addEventListener('click', (event) => {
      const action = event.target.closest('button')?.dataset.subscriptionAction;
      if (action === 'confirm') {
        const control = this.getPendingSubscriptionControl();
        if (!control || control.querySelector('button').disabled) {
          this.closeSubscriptionConfirmation();
          return;
        }
        if (this.pendingSubscription.signature !== this.subscriptionSignature(control)) {
          this.updateSubscriptionConfirmation(control);
          return;
        }
        this.closeSubscriptionConfirmation();
        control.changeSellingPlan();
      } else if (action === 'keep' || action === 'close') {
        this.closeSubscriptionConfirmation();
      } else if (event.target === dialog) {
        const rect = dialog.getBoundingClientRect();
        if (
          event.clientX < rect.left || event.clientX > rect.right ||
          event.clientY < rect.top || event.clientY > rect.bottom
        ) this.closeSubscriptionConfirmation();
      }
    });
  }

  subscriptionSignature(control) {
    return JSON.stringify([
      control.dataset.quantity,
      control.dataset.confirmationPrice,
      control.dataset.confirmationKeep,
      control.dataset.confirmationSwitch,
    ]);
  }

  handleLegacySubscriptionConfirmation(event) {
    if (!event.isTrusted || !(event.target instanceof Element)) return;
    const button = event.target.closest(
      '#hv-subscription-confirmation[open] button[data-action="confirm"]',
    );
    if (!button) return;

    const approval = { dialog: button.closest('dialog') };
    this.legacySubscriptionConfirmation = approval;
    setTimeout(() => {
      if (this.legacySubscriptionConfirmation === approval) {
        this.legacySubscriptionConfirmation = null;
      }
    }, 0);
  }

  consumeLegacySubscriptionConfirmation(event) {
    const approval = this.legacySubscriptionConfirmation;
    if (
      !approval ||
      event.isTrusted ||
      approval.dialog.open ||
      event.currentTarget !== document.activeElement
    ) return false;

    this.legacySubscriptionConfirmation = null;
    return true;
  }

  getPendingSubscriptionControl() {
    if (!this.pendingSubscription) return null;
    return Array.from(this.querySelectorAll('#CartDrawer cart-subscription-switch')).find(
      (control) =>
        control.dataset.key === this.pendingSubscription.key &&
        control.dataset.currentSellingPlanKey === this.pendingSubscription.plan &&
        control.requiresSubscriptionConfirmation(),
    );
  }

  updateSubscriptionConfirmation(control) {
    const dialog = this.subscriptionConfirmation;
    dialog.querySelector('[data-subscription-price-description]').textContent =
      control.dataset.confirmationPrice || dialog.dataset.defaultPrice;
    dialog.querySelector('[data-subscription-action="keep"]').textContent =
      control.dataset.confirmationKeep || dialog.dataset.defaultKeep;
    dialog.querySelector('[data-subscription-action="confirm"]').textContent =
      control.dataset.confirmationSwitch || dialog.dataset.defaultSwitch;
    this.pendingSubscription.signature = this.subscriptionSignature(control);
  }

  openSubscriptionConfirmation(control) {
    const dialog = this.subscriptionConfirmation;
    if (!dialog || dialog.open || !control.isConnected) return;
    this.ignoreSubscriptionEscape = false;
    this.pendingSubscription = {
      key: control.dataset.key,
      plan: control.dataset.currentSellingPlanKey,
      button: control.querySelector('button'),
    };
    this.updateSubscriptionConfirmation(control);
    removeTrapFocus();
    dialog.showModal();
    dialog.querySelector('[data-subscription-action="keep"]').focus();
  }

  refreshSubscriptionConfirmation() {
    if (!this.subscriptionConfirmation?.open) return;
    const control = this.getPendingSubscriptionControl();
    if (!control) {
      this.closeSubscriptionConfirmation();
      return;
    }
    this.updateSubscriptionConfirmation(control);
    removeTrapFocus();
  }

  closeSubscriptionConfirmation(restoreFocus = true) {
    const dialog = this.subscriptionConfirmation;
    if (!dialog?.open) return;
    const trigger = this.getPendingSubscriptionControl()?.querySelector('button') ||
      this.pendingSubscription?.button;
    this.pendingSubscription = null;
    dialog.close();
    if (restoreFocus && this.isConnected && this.classList.contains('active')) {
      this.restoreCartFocus(trigger);
    }
  }

  restoreCartFocus(preferredElement) {
    if (this.subscriptionConfirmation?.open || !this.classList.contains('active')) return;
    const container = this.querySelector('#CartDrawer');
    const target = preferredElement?.isConnected && !preferredElement.disabled
      ? preferredElement
      : this.querySelector('.drawer__inner');
    if (container) trapFocus(container, target);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleLegacySubscriptionConfirmation, true);
    this.legacySubscriptionConfirmation = null;
    this.closeSubscriptionConfirmation(false);
  }

  captureSubscriptionPriceChange(control, variantId, sellingPlanKey) {
    const price = control.closest('.cart-item')?.querySelector('[data-cart-item-price]');
    return {
      cents: price ? Number(price.dataset.cartItemPrice) : null,
      variantId: String(variantId),
      sellingPlanKey: sellingPlanKey || 'one_time',
      quantity: control.dataset.quantity,
      switchType: control.dataset.switchType,
    };
  }

  completeSubscriptionPriceChange(transition) {
    if (!transition || !this.isConnected) return;
    const rows = Array.from(this.querySelectorAll('#CartDrawer [data-cart-parent]')).filter(
      (row) => row.dataset.parentVariantId === transition.variantId &&
        row.dataset.parentSellingPlanKey === transition.sellingPlanKey,
    );
    if (rows.length !== 1) {
      this.restoreCartFocus();
      return;
    }
    const row = rows[0];
    const controls = Array.from(row.querySelectorAll('cart-subscription-switch'));
    const control = controls.find((item) => item.dataset.switchType === transition.switchType) || controls[0];
    this.restoreCartFocus(control?.querySelector('button') || row.querySelector('button'));
    if (transition.cents === null || control?.dataset.quantity !== transition.quantity) return;
    const price = row.querySelector('[data-cart-item-price]');
    if (price) this.animateSubscriptionPrice(transition.cents, price);
  }

  subscriptionPriceFormatter(price) {
    const format = this.subscriptionConfirmation?.dataset.moneyFormat;
    const token = format?.match(/\{\{\s*(\w+)\s*\}\}/);
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.'],
    };
    if (!token || !formats[token[1]]) return null;
    const [precision, thousands, decimal] = formats[token[1]];
    const formatAmount = (cents) => {
      const parts = (cents / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    const text = price.textContent;
    const amount = formatAmount(Number(price.dataset.cartItemPrice));
    if (!text.includes(amount)) return null;
    return (cents) => text.replace(amount, formatAmount(cents));
  }

  animateSubscriptionPrice(from, price) {
    if (
      !price.isConnected ||
      price.classList.contains('cart-item__price-count') ||
      price.querySelector('.hv-price-count')
    ) return;

    const to = Number(price.dataset.cartItemPrice);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const format = this.subscriptionPriceFormatter(price);
    if (!Number.isFinite(from) || !Number.isFinite(to) || from === to || reducedMotion.matches || !format) return;

    const original = Array.from(price.childNodes);
    const actual = document.createElement('span');
    actual.className = 'cart-item__price-count-actual';
    actual.append(...original);
    const sizer = document.createElement('span');
    sizer.className = 'cart-item__price-count-sizer';
    sizer.setAttribute('aria-hidden', 'true');
    sizer.textContent = format(from);
    const visual = document.createElement('span');
    visual.className = 'cart-item__price-count-visual';
    visual.setAttribute('aria-hidden', 'true');
    visual.textContent = format(from);
    price.classList.add('cart-item__price-count');
    price.append(actual, sizer, visual);

    const start = performance.now();
    const tick = (now) => {
      if (!price.isConnected) return;
      const progress = Math.min(1, Math.max(0, (now - start) / 650));
      if (progress === 1 || reducedMotion.matches) {
        price.replaceChildren(...original);
        price.classList.remove('cart-item__price-count');
        return;
      }
      visual.textContent = format(from + Math.trunc((to - from) * progress));
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  handleCheckoutSubmit(event) {
    if (this.checkoutReady || event.target.id !== 'CartDrawer-Form') {
      return;
    }

    event.preventDefault();
    this.prepareCheckout(() => {
      const form = this.querySelector('#CartDrawer-Form');
      if (!form) throw new Error('Cart checkout form not found');

      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = event.submitter?.value || 'Checkout';
      form.appendChild(checkoutInput);

      this.checkoutReady = true;
      HTMLFormElement.prototype.submit.call(form);
    });
  }

  handleExpressCheckoutClick(event) {
    const checkoutLink = event.target.closest(
      'a.cart__checkout-button.button--shop-pay',
    );
    if (!checkoutLink || this.checkoutReady) return;

    event.preventDefault();
    const checkoutUrl = checkoutLink.href;
    this.prepareCheckout(() => {
      this.checkoutReady = true;
      window.location.assign(checkoutUrl);
    });
  }

  async prepareCheckout(onReady) {
    if (this.checkoutPreparationPromise) return;

    this.setCheckoutLoading(true);
    this.checkoutPreparationPromise = (async () => {
      if (typeof CartItems === 'undefined') {
        throw new Error('Cart recovery is unavailable');
      }

      await CartItems.waitForCartReady();
      const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Unable to check the cart subtotal');

      const cart = await response.json();
      if (!Number.isFinite(cart.total_price)) {
        throw new Error('Invalid cart subtotal');
      }

      this.setCheckoutLoading(false);
      if (!this.updateCheckoutMinimum(cart.total_price, cart.item_count)) return;

      onReady();
    })();

    try {
      await this.checkoutPreparationPromise;
    } catch (error) {
      this.checkoutReady = false;
      console.error('[CartCheckout] Cart is not ready:', error);
      const errors =
        document.getElementById('CartDrawer-CartErrors') ||
        document.getElementById('cart-errors');
      if (errors) {
        errors.classList.remove('hidden');
        errors.textContent =
          window.cartStrings?.error || 'Unable to update your cart.';
      }
      this.setCheckoutLoading(false);
    } finally {
      this.checkoutPreparationPromise = null;
    }
  }

  updateCheckoutMinimum(subtotal, itemCount = 1) {
    const form = this.querySelector('#CartDrawer-Form');
    const minimum = Number(form?.dataset.minimumCheckoutSubtotal);
    if (!Number.isFinite(minimum) || minimum < 0) {
      throw new Error('Invalid minimum checkout subtotal');
    }

    const belowMinimum = subtotal < minimum;
    const checkoutDisabled = itemCount === 0 || belowMinimum;
    this.querySelectorAll('[data-cart-minimum-message]').forEach((message) => {
      message.hidden = itemCount === 0 || !belowMinimum;
    });
    this.querySelectorAll('.cart__checkout-button').forEach((control) => {
      if (control.tagName === 'BUTTON') {
        control.disabled = checkoutDisabled;
      } else if (checkoutDisabled) {
        control.removeAttribute('href');
        control.setAttribute('aria-disabled', 'true');
      } else {
        control.setAttribute('href', control.dataset.checkoutUrl);
        control.removeAttribute('aria-disabled');
      }
    });

    return !checkoutDisabled;
  }

  setCheckoutLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    if (isLoading) {
      this.setAttribute('aria-busy', 'true');
    } else if (!this.classList.contains('is-reconciling')) {
      this.removeAttribute('aria-busy');
    }
    this.querySelectorAll('.cart__checkout-button').forEach((control) => {
      if (control.tagName === 'BUTTON') {
        if (isLoading && !control.disabled) {
          control.dataset.disabledByCheckoutPreparation = 'true';
          control.disabled = true;
        } else if (
          !isLoading &&
          control.dataset.disabledByCheckoutPreparation === 'true'
        ) {
          control.disabled = false;
          delete control.dataset.disabledByCheckoutPreparation;
        }
        return;
      }

      if (isLoading) {
        control.setAttribute('aria-disabled', 'true');
      } else if (control.hasAttribute('href')) {
        control.removeAttribute('aria-disabled');
      }
    });
  }

  setInitialRecoveryLoading(isLoading) {
    this.classList.toggle('is-reconciling', isLoading);
    if (isLoading) {
      this.setAttribute('aria-busy', 'true');
    } else if (!this.checkoutPreparationPromise) {
      this.removeAttribute('aria-busy');
    }
  }

  handleKitUpsellChange(event) {
    const toggle = event.target.closest('[data-cart-kit-subscription-toggle]');
    if (!toggle) return;

    const kitUpsell = toggle.closest('[data-cart-kit-upsell]');
    if (!kitUpsell) return;

    const isSubscription = toggle.checked;
    const upgradeText = kitUpsell.querySelector('[data-cart-kit-upgrade-text]');
    const savingsBadge = kitUpsell.querySelector(
      '[data-cart-kit-savings-badge]',
    );
    const comparePrice = kitUpsell.querySelector(
      '[data-cart-kit-compare-price]',
    );
    const price = kitUpsell.querySelector('[data-cart-kit-price]');

    if (upgradeText) {
      upgradeText.textContent = isSubscription
        ? kitUpsell.dataset.subscriptionUpgradeText
        : kitUpsell.dataset.oneTimeUpgradeText;
    }

    if (savingsBadge) {
      savingsBadge.hidden = !isSubscription;
    }

    if (comparePrice) {
      comparePrice.hidden = !isSubscription;
    }

    if (price) {
      price.textContent = isSubscription
        ? kitUpsell.dataset.subscriptionPrice
        : kitUpsell.dataset.oneTimePrice;
    }

    kitUpsell.classList.toggle('is-one-time', !isSubscription);
  }

  connectedCallback() {
    document.addEventListener('click', this.handleLegacySubscriptionConfirmation, true);
    if (this.hasAttribute('data-open-on-load')) {
      this.open();
      return;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('#cart-icon-bubble');
    if (!cartLinks) return;

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        if (!cartLink.hasAttribute('href')) return;
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      window.slideCartOpened = true;
    });

    this.addEventListener(
      'transitionend',
      () => {
        if (this.subscriptionConfirmation?.open) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.closeSubscriptionConfirmation(false);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.querySelector('header__search details[open]')
      ? ''
      : document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open'),
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, { open = true } = {}) {
    if (!parsedState.sections) {
      console.error('No sections returned', parsedState);

      this.update({ open });
      return;
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElements = document.querySelectorAll(
        section.selector || `#${section.id}`,
      );

      if (!sectionElements.length) return;
      const innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
      sectionElements.forEach((sectionElement) => {
        sectionElement.innerHTML = innerHTML;
      });
    });
    this.refreshSubscriptionConfirmation();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener(
        'click',
        this.close.bind(this),
      );
      if (open) this.open();
    });
  }

  update({ open = true } = {}) {
    const body = JSON.stringify({
      updates: {},
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.json();
      })
      .then((state) => {
        this.renderContents(state, { open });
        this.classList.toggle('is-empty', state.item_count === 0);
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const element = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

window.addEventListener('pageshow', (event) => {
  const navigationEntry = performance.getEntriesByType('navigation')[0];
  const isHistoryNavigation =
    event.persisted || navigationEntry?.type === 'back_forward';

  if (!isHistoryNavigation) return;

  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return;

  cartDrawer.update({ open: false }).catch((error) => {
    console.error('[CartDrawer] Failed to refresh after page restore:', error);
  });
});

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    const form = this.querySelector('#CartDrawer-Form');
    if (!form) return;
    this.closest('cart-drawer')?.updateCheckoutMinimum(
      Number(form.dataset.cartSubtotal),
      Number(form.dataset.cartItemCount),
    );
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
